import { useEffect, useState, RefObject } from 'react';

export type TooltipVariant = 'default' | 'top' | 'bottom' | 'left' | 'right';

interface TooltipPositionConfig {
  triggerRef: RefObject<HTMLElement | null>;
  tooltipRef: RefObject<HTMLSpanElement | null>;
  variant: TooltipVariant;
  offset?: number;
  isVisible: boolean;
  content: React.ReactNode;
}

interface PositionStyle {
  top?: number;
  bottom?: number;
  left?: number;
  right?: number;
  transform?: string;
}

const PADDING = 8;
const ESTIMATED_CHAR_WIDTH = 7;
const ESTIMATED_BASE_WIDTH = 20;
const MAX_TOOLTIP_WIDTH = 250;
const ESTIMATED_HEIGHT = 28;

/**
 * Calculate estimated tooltip dimensions before render
 */
const estimateTooltipSize = (content: React.ReactNode) => {
  const contentText = typeof content === 'string' ? content : '';
  const width = Math.min((contentText.length * ESTIMATED_CHAR_WIDTH) + ESTIMATED_BASE_WIDTH, MAX_TOOLTIP_WIDTH);
  return { width, height: ESTIMATED_HEIGHT };
};

/**
 * Check available space around trigger element
 */
const getAvailableSpace = (rect: DOMRect) => ({
  top: rect.top,
  bottom: window.innerHeight - rect.bottom,
  left: rect.left,
  right: window.innerWidth - rect.right,
});

/**
 * Auto-adjust variant based on available space
 */
const adjustVariantForSpace = (
  variant: TooltipVariant,
  availableSpace: ReturnType<typeof getAvailableSpace>,
  estimatedSize: { width: number; height: number }
): TooltipVariant => {
  if (variant === 'top' && availableSpace.top < estimatedSize.height + PADDING) {
    return 'bottom';
  }
  if (variant === 'bottom' && availableSpace.bottom < estimatedSize.height + PADDING) {
    return 'top';
  }
  if (variant === 'right' && availableSpace.right < estimatedSize.width + PADDING) {
    return 'left';
  }
  if (variant === 'left' && availableSpace.left < estimatedSize.width + PADDING) {
    return 'right';
  }
  return variant;
};

/**
 * Calculate position for top/bottom variants
 */
const calculateHorizontalPosition = (
  rect: DOMRect,
  estimatedWidth: number,
  isTop: boolean,
  offset: number
): PositionStyle => {
  const centerX = rect.left + rect.width / 2;
  const minLeft = estimatedWidth / 2 + PADDING;
  const maxLeft = window.innerWidth - estimatedWidth / 2 - PADDING;
  const adjustedLeft = Math.max(minLeft, Math.min(centerX, maxLeft));

  return {
    [isTop ? 'bottom' : 'top']: isTop 
      ? window.innerHeight - rect.top + offset
      : rect.bottom + offset,
    left: adjustedLeft,
    transform: 'translateX(-50%)',
  };
};

/**
 * Calculate position for left/right variants
 */
const calculateVerticalPosition = (
  rect: DOMRect,
  estimatedHeight: number,
  isLeft: boolean,
  offset: number
): PositionStyle => {
  const centerY = rect.top + rect.height / 2;
  const minTop = estimatedHeight / 2 + PADDING;
  const maxTop = window.innerHeight - estimatedHeight / 2 - PADDING;
  const adjustedTop = Math.max(minTop, Math.min(centerY, maxTop));

  return {
    top: adjustedTop,
    [isLeft ? 'right' : 'left']: isLeft
      ? window.innerWidth - rect.left + offset
      : rect.right + offset,
    transform: 'translateY(-50%)',
  };
};

/**
 * Calculate initial tooltip position based on variant
 */
const calculatePosition = (
  rect: DOMRect,
  variant: TooltipVariant,
  estimatedSize: { width: number; height: number },
  offset: number
): PositionStyle => {
  switch (variant) {
    case 'top':
      return calculateHorizontalPosition(rect, estimatedSize.width, true, offset);
    case 'bottom':
    case 'default':
      return calculateHorizontalPosition(rect, estimatedSize.width, false, offset);
    case 'left':
      return calculateVerticalPosition(rect, estimatedSize.height, true, offset);
    case 'right':
      return calculateVerticalPosition(rect, estimatedSize.height, false, offset);
    default:
      return calculateHorizontalPosition(rect, estimatedSize.width, false, offset);
  }
};

/**
 * Refine position after tooltip renders with actual dimensions
 */
const refinePosition = (
  currentStyle: PositionStyle,
  tooltipRect: DOMRect
): PositionStyle => {
  const refinedStyle = { ...currentStyle };
  const actualWidth = tooltipRect.width;
  const actualHeight = tooltipRect.height;

  // Adjust horizontal overflow
  if (refinedStyle.left !== undefined) {
    const tooltipLeft = refinedStyle.left - actualWidth / 2;
    const tooltipRight = refinedStyle.left + actualWidth / 2;

    if (tooltipLeft < PADDING) {
      refinedStyle.left = actualWidth / 2 + PADDING;
    } else if (tooltipRight > window.innerWidth - PADDING) {
      refinedStyle.left = window.innerWidth - actualWidth / 2 - PADDING;
    }
  }

  // Adjust vertical overflow
  if (refinedStyle.top !== undefined) {
    const tooltipTop = refinedStyle.top - actualHeight / 2;
    const tooltipBottom = refinedStyle.top + actualHeight / 2;

    if (tooltipTop < PADDING) {
      refinedStyle.top = actualHeight / 2 + PADDING;
    } else if (tooltipBottom > window.innerHeight - PADDING) {
      refinedStyle.top = window.innerHeight - actualHeight / 2 - PADDING;
    }
  }

  return refinedStyle;
};

/**
 * Custom hook to calculate and manage tooltip position
 * 
 * Handles:
 * - Dynamic position calculation based on variant
 * - Viewport boundary detection and adjustment
 * - Auto-repositioning on scroll/resize
 * - Position refinement after render
 */
export const useTooltipPosition = ({
  triggerRef,
  tooltipRef,
  variant,
  offset = PADDING,
  isVisible,
  content,
}: TooltipPositionConfig) => {
  const [tooltipStyle, setTooltipStyle] = useState<React.CSSProperties>({});

  useEffect(() => {
    if (!content || !isVisible || !triggerRef.current) {
      setTooltipStyle({});
      return;
    }

    const updatePosition = () => {
      if (!triggerRef.current) return;

      const rect = triggerRef.current.getBoundingClientRect();
      const estimatedSize = estimateTooltipSize(content);
      const availableSpace = getAvailableSpace(rect);
      const finalVariant = adjustVariantForSpace(variant, availableSpace, estimatedSize);
      const style = calculatePosition(rect, finalVariant, estimatedSize, offset);

      setTooltipStyle(style);

      // Refine position after tooltip renders with actual dimensions
      requestAnimationFrame(() => {
        if (tooltipRef.current && triggerRef.current) {
          const tooltipRect = tooltipRef.current.getBoundingClientRect();
          const refinedStyle = refinePosition(style, tooltipRect);
          setTooltipStyle(refinedStyle);
        }
      });
    };

    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [content, isVisible, variant, offset, triggerRef, tooltipRef]);

  return tooltipStyle;
};
